import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { getAdminInfo } from "../../core/admin.core";
import orderCore from "../../core/order.core";
import { deleteCustomerEyeInfo, getCustomerEyeInfo, listCustomerInfoRecords } from "../../core/posCustomer.core";
import { useTranslation } from "../../core/translation.core";
import { useEditCustomerEyeInfo } from "../../hooks/services/useCustomers";
import BackIcon from "../../icons/back.icon";
import ChevronDoubleRightIcon from "../../icons/chevronDoubleRight.icon";
import { CheckoutUser } from "../../types/checkout";
import { AddCustomerInfo, ShopFull } from "../../types/retailShop";
import { PosCustomerInfo, PosUserEyeInfo } from "../../types/user";
import CustomerEyeInfoPanel from "./CustomerEyeInfoPanel.comp";
import EyesInfoEditor from "./EyesInfoEditor";
import PlusIcon from "../../icons/posicons/plus.icon";
import { PosModal } from "../reuseables/modal.comp";
import TrashIcon from "../../icons/trash.icon";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import DocumentDuplicateIcon from "../../icons/posicons/documentDuplicate.icon";
import { Order } from "../../types/order";

interface CustomerInfoPanelProps {
  customer?: CheckoutUser | null;
  customerInfo?: PosCustomerInfo | null;
  adminShop?: ShopFull;
  placement?: "default" | "order";
  order?: Order;
}

export default function CustomerInfoPanel({ customer, order, adminShop, placement = "default" }: CustomerInfoPanelProps) {
  if (!adminShop) return null;

  return (
    <section>
      {adminShop.addCustomerInfo.map((i, idx) => (
        <InfoPanel key={i} idx={idx} type={i} customer={customer} order={order} placement={placement} />
      ))}
    </section>
  );
}

interface InfoPanelProps {
  type: AddCustomerInfo;
  customer?: CheckoutUser | null;
  idx: number;
  placement: "default" | "order";
  order?: Order;
}

const InfoPanel = ({ type, customer, order, idx, placement }: InfoPanelProps) => {
  const [id, setId] = useState<string | null>("");
  const { t, msg } = useTranslation();
  const [showFull, setShowFull] = useState(idx === 0);
  const router = useRouter();
  const [isCreate, setIsCreate] = useState(false);
  const [selectedEyeInfoId, setSelectedEyeInfoId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copyEyeInfo, setCopyEyeInfo] = useState<PosUserEyeInfo | null>(null);

  useEffect(() => {
    if (
      order &&
      order.customer &&
      order.customer.customerInfo &&
      order.customer.customerInfo.eyeInfo &&
      order.customer.customerInfo.eyeInfo.id
    ) {
      setSelectedEyeInfoId(order.customer.customerInfo.eyeInfo.id);
    } else {
      setSelectedEyeInfoId(null);
    }
  }, [order]);

  async function handleSelectEyeInfo(data: PosUserEyeInfo) {
    setSelectedEyeInfoId(data.id);
    await orderCore.updateOrderEyeInfo({ eyeInfo: data, orderId: router.query.orderId as string });
  }

  const { data } = useQuery({
    enabled: customer !== undefined && customer !== null,
    queryFn: () => listCustomerInfoRecords(customer!.id),
    queryKey: ["customerInfo", customer!.id],
  });

  const { data: eyeInfo } = useQuery({
    enabled: id !== "" && id !== null && id !== undefined && customer !== undefined && customer !== null,
    queryFn: () => getCustomerEyeInfo({ id: id!, userId: customer!.id }),
    queryKey: ["customerEyeInfo", id, customer?.id],
  });

  function handleShowDeleteModal() {
    setShowDeleteModal(true);
  }

  async function deleteEyeInfo() {
    try {
      await deleteCustomerEyeInfo({ id: id! });

      setId(null);
      setShowDeleteModal(false);
      toast.success("Eye Info Deleted");
      router.reload();
    } catch (error) {
      console.log(error);
      setShowDeleteModal(false);
    }
  }

  switch (type) {
    case "eyeInfo":
      return (
        <section className="space-y-10">
          {!id && !isCreate && (
            <>
              <div className="flex justify-between items-center">
                <h1 className="font-bold">{t(msg.customer.eyeRecords)}</h1>

                <button onClick={() => setIsCreate(true)} className="bg-pos-blue text-white p-1 rounded-full">
                  <PlusIcon />
                </button>
              </div>

              <div
                className={`grid border-b pb-1  place-items-center ${placement === "order" ? "grid-cols-5 " : "grid-cols-4 "}`}
              >
                <p>{t(msg.order.date)}</p>
                <p className="col-span-2">{t(msg.form.updatedBy)}</p>
                <p></p>
              </div>

              {data?.eyeInfo
                ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((info) => (
                  <div
                    key={info.id}
                    className={`eye-info-item grid place-items-center 
                      ${selectedEyeInfoId === info.id ? "border-2 py-3 border-blue-600 text-white" : ""}
                      ${placement === "order" ? "grid-cols-5 " : "grid-cols-4 "}`}
                  >
                    <p className="text-sm font-bold">{format(new Date(info.createdAt), "yyyy-MM-dd HH:mm")}</p>
                    <p className="col-span-2">{info?.updatedByAdminId?.email || info?.createdByAdminId?.email}</p>
                    <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => {
                          console.log("setInfo", info);
                          setId(info.id);
                        }}
                        className="button-eyeinfo-view text-pos-dark-blue cursor-pointer font-medium"
                      >
                        {t(msg.pos.view)}
                      </button>
                      <button
                        id="copy-eye-info"
                        type="button"
                        onClick={async () => {
                          const eyeInfoData = await getCustomerEyeInfo({ id: info.id, userId: customer!.id });
                          setCopyEyeInfo(eyeInfoData.eyeInfo);
                          setIsCreate(true);
                        }}
                        className="text-pos-dark-blue cursor-pointer font-medium flex items-center gap-1"
                        title="Copy"
                      >
                        <DocumentDuplicateIcon className="w-5 h-5" />
                        <span>Copy</span>
                      </button>
                    </div>

                    {placement === "order" && (
                      <button onClick={() => handleSelectEyeInfo(info)} className="bg-pos-blue text-white px-4 py-1 rounded-md">
                        {selectedEyeInfoId === info.id ? "Selected" : t(msg.pos.select)}
                      </button>
                    )}
                  </div>
                ))}
            </>
          )}

          {placement === "order" && selectedEyeInfoId && !id && (
            <div className="flex justify-end ">
              {/* @ts-ignore */}
              <button onClick={() => handleSelectEyeInfo(null)} className="bg-red-500 text-white px-4 py-1 rounded-md">
                Unselect
              </button>
            </div>
          )}

          {id && (
            <section className="space-y-8">
              <div className="flex items-center justify-between">
                <button id="eyeinfo-back-button" type="button" onClick={() => setId(null)}>
                  <ChevronLeftIcon className="w-6 h-6 text-pos-blue" />
                </button>

                {placement === "default" && (
                  <button type="button" id="delete-eye-info" onClick={handleShowDeleteModal}>
                    <TrashIcon className="w-6 h-6 text-red-500" />
                  </button>
                )}
              </div>

              <div className="border rounded-lg">
                <div className="flex justify-between items-center cursor-pointer p-4" onClick={() => setShowFull(!showFull)}>
                  <span className="text-2xl font-bold">{t(msg.pos.eyesInfo)}</span>
                  <ChevronDoubleRightIcon className={`w-6 h-6 transition-all ${showFull ? "rotate-90" : "right-0"}`} />
                </div>

                {showFull ? (
                  <div className="p-4">
                    {/* @ts-ignore */}
                    <CustomerEyeInfoPanel id={customer.id} eyeInfo={eyeInfo?.eyeInfo} placement={placement} />
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {/* @ts-ignore */}
          {isCreate && customer && (
            <CreateEyeRecord
              userId={customer.id}
              setIsCreate={() => {
                setIsCreate(false);
                setCopyEyeInfo(null);
              }}
              initialData={copyEyeInfo}
            />
          )}

          {showDeleteModal && (
            <PosModal setModalOpened={setShowDeleteModal}>
              <div className="mb-10 flex flex-col justify-center items-center text-center">
                <h2 className="font-bold text-2xl text-primary">{t(msg.checkout.areYouSure)}</h2>
                <p>{t(msg.pos.youAreAboutToDeleteEyeInfo)}</p>
                <p>{t(msg.checkout.makeSureAction)}</p>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  id="delete-eye-info-cancel"
                  onClick={() => setShowDeleteModal(false)}
                  className="outline px-5 py-2 hover:bg-gray-400 hover:text-white"
                >
                  {t(msg.form.cancel)}
                </button>
                <button id="delete-eye-info-confirm" onClick={deleteEyeInfo} className="outline px-5 py-2 bg-red-400 text-white">
                  {t(msg.form.delete)}
                </button>
              </div>
            </PosModal>
          )}
        </section>
      );

    default:
      return null;
  }
};

function CreateEyeRecord({
  userId,
  setIsCreate,
  initialData,
}: {
  userId: string;
  setIsCreate: () => void;
  initialData?: PosUserEyeInfo | null;
}) {
  const methods = useForm();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isEyePanelOpen, setIsEyePanelOpen] = useState(false);
  const placement = "default";

  const editCustomerEyeInfo = useEditCustomerEyeInfo();

  const { data: me } = useQuery({
    queryFn: () => getAdminInfo(),
    queryKey: ["admin"],
  });

  function handleToggleEyePanel() {
    setIsEyePanelOpen(!isEyePanelOpen);
  }

  useEffect(() => {
    if (initialData) {
      // Remove id and other non-form fields before resetting
      const { optometrist, ...formData } = initialData;
      // Convert optometrist object to optometristId string and remove non-form fields
      const dataToReset: any = {};
      Object.keys(formData).forEach((key) => {
        if (!["id", "createdAt", "updatedAt", "createdByAdminId", "updatedByAdminId"].includes(key)) {
          dataToReset[key] = formData[key as keyof typeof formData];
        }
      });
      dataToReset.optometristId = optometrist?.id || "";
      methods.reset(dataToReset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  async function onSubmit(data: any) {
    const filteredData: any = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== null && value !== undefined && value !== "")
    );

    try {
      const { optometristId, ...restData } = filteredData;
      const payload = {
        data: {
          ...restData,
          ...(optometristId ? { optometrist: optometristId } : {}),
          createdByAdminId: me._id,
        },
        userId,
      };

      if (placement === "default") {
        await editCustomerEyeInfo.mutateAsync(payload);
      } else {
        await orderCore.updateOrderEyeInfo({ eyeInfo: filteredData, orderId: router.query.orderId as string });

        if (data.isSave) {
          await editCustomerEyeInfo.mutateAsync(payload);
        }

        queryClient.invalidateQueries({ queryKey: ["customerEyeInfo"] });
        queryClient.invalidateQueries({ queryKey: ["customerInfo"] });
        queryClient.invalidateQueries({ exact: false, queryKey: ["order"] });
      }

      // setIsEyePanelOpen(false);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (editCustomerEyeInfo.isError) {
      toast.error("Update Failed");
      editCustomerEyeInfo.reset();
    }
    if (editCustomerEyeInfo.isSuccess) {
      toast.success("Updated successfully");
      editCustomerEyeInfo.reset();
      queryClient.invalidateQueries({ queryKey: ["customerEyeInfo"] });
      queryClient.invalidateQueries({ queryKey: ["customerInfo"] });
      router.reload();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCustomerEyeInfo.isSuccess, editCustomerEyeInfo.error]);

  return (
    <section className="space-y-8">
      <button type="button" onClick={setIsCreate} id="eyeinfo-back-button">
        <BackIcon className="w-6 h-6 text-pos-blue" />
      </button>

      <h1
        id={initialData ? "copy-eye-record-title" : "create-eye-record-title"}
        className="font-bold text-center text-lg bg-slate-200 dark:bg-gray-500 p-3"
      >
        {initialData ? "Copy Eye Record" : "Create New Eye Record"}
      </h1>

      <EyesInfoEditor
        handleSubmit={onSubmit} //
        methods={methods}
        placement={"default"}
        handleCancel={handleToggleEyePanel}
      />
    </section>
  );
}
